import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const BAND = { hot: "#FF3B30", warm: "#FF9F0A", moderate: "#FFD60A", cold: "#636366", no_data: "#1C1C1E", blind_spot: "#2C2C2E" };
const STATUS = { VERIFIED: "#30D158", REVIEW: "#FF9F0A", UNVERIFIED: "#8E8E93" };
const box = "border border-white/10 bg-[#121214] p-4";
const h2 = "font-display font-bold text-sm mb-3";

function Customers({ d }) {
  const max = Math.max(1, ...d.hourly.map((h) => h.in));
  return (
    <div className={box} data-testid="avd-customers">
      <div className={h2}>Customers today</div>
      <div className="flex items-end gap-6 mb-3">
        <div><div className="font-display font-black text-3xl" data-testid="avd-customer-count">{d.customers}</div><div className="text-[10px] text-[#71717A] font-mono-data">real customers in (staff excluded)</div></div>
        <div><div className="font-display font-bold text-xl text-[#A1A1AA]" data-testid="avd-7day-avg">{d.customers_7day_avg}</div><div className="text-[10px] text-[#71717A] font-mono-data">7-day average</div></div>
        <div><div className="font-display font-bold text-xl text-[#A1A1AA]">{d.unique_sessions}</div><div className="text-[10px] text-[#71717A] font-mono-data">anonymous sessions</div></div>
        <div><div className="font-display font-bold text-xl text-[#FFD60A]" data-testid="avd-workers-count">{d.workers_in ?? 0}</div><div className="text-[10px] text-[#71717A] font-mono-data">workers / vendors excluded</div></div>
      </div>
      <div className="text-[10px] font-mono-data text-[#71717A] mb-2" data-testid="avd-by-door">
        by entrance: {Object.entries(d.customers_by_door || {}).map(([k, v]) => `${DOOR[k] || k} ${v}`).join(" · ") || "none yet"}
      </div>
      <div className="flex items-end gap-[3px] h-16" data-testid="avd-hourly">
        {d.hourly.slice(7, 21).map((h) => (
          <div key={h.hour} className="flex-1 flex flex-col items-center justify-end h-full">
            <div className="w-full bg-[#0A84FF]" style={{ height: `${(h.in / max) * 100}%`, minHeight: h.in ? 2 : 0 }} title={`${h.hour}:00 — ${h.in} in`} />
            <div className="text-[9px] text-[#52525B] font-mono-data">{h.hour}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const DOOR = { "AV-OFF-02": "Front", "AV-WH-06": "Warehouse side", "AV-DK-01": "Dock 1", "AV-DK-02": "Dock 2" };

function Workers({ items, onChange }) {
  const [img, setImg] = useState({});
  if (!items.length) return null;
  const show = async (id) => {
    try { const r = await api.get(`/avstone/workers/${id}/photo`); setImg((m) => ({ ...m, [id]: r.data.image })); } catch { toast("No photo kept"); }
  };
  const isCustomer = async (id) => { await api.post(`/avstone/workers/${id}/customer`); toast("Counted as a customer"); onChange(); };
  return (
    <div className={box} data-testid="avd-workers">
      <div className={h2}>Workers / vendors today — not counted as customers</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {items.map((w) => (
          <div key={w.id} className="border border-white/10 p-2 text-[11px] font-mono-data flex gap-2" data-testid={`avd-worker-${w.id}`}>
            {img[w.id] ? <img src={img[w.id]} alt="worker" className="w-16 h-20 object-cover" />
              : <button onClick={() => show(w.id)} disabled={w.source === "face"} className="w-16 h-20 border border-white/10 text-[#71717A] disabled:opacity-40" data-testid={`avd-worker-photo-${w.id}`}>photo</button>}
            <div className="min-w-0">
              <div className="text-white">{new Date(w.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · {DOOR[w.camera_id] || w.camera_id}</div>
              <div className="text-[#FFD60A] truncate">{w.kind || "worker"}</div>
              <div className="text-[#71717A]">{w.source === "face" ? "saved vendor face" : "AI photo check"}</div>
              {w.source !== "face" && <button onClick={() => isCustomer(w.id)} className="mt-1 text-[#0A84FF] underline" data-testid={`avd-worker-is-customer-${w.id}`}>actually a customer</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ZoneTile({ z }) {
  const hatched = z.band === "blind_spot";
  return (
    <div data-testid={`avd-zone-${z.zone_id}`} className="border border-white/10 px-2 py-1.5 text-[10px] font-mono-data"
      style={{ background: hatched ? "repeating-linear-gradient(45deg,#2C2C2E 0 6px,#121214 6px 12px)" : BAND[z.band] + (z.band === "no_data" ? "" : "33"), borderColor: BAND[z.band] }}>
      <div className="font-bold text-white">{z.zone_id}</div>
      <div className="text-[#A1A1AA]">{hatched ? "blind spot" : z.band === "no_data" ? "no data" : `${z.band} · ${Math.round(z.total_dwell_s)}s · ${z.visits} visits`}</div>
    </div>
  );
}

function CamGrid({ g }) {
  const cells = g.cells || {};
  const mx = Math.max(1, ...Object.values(cells));
  return (
    <div data-testid={`avd-grid-${g.camera_id}`}>
      <div className="text-[10px] font-mono-data text-[#71717A] mb-1">{g.camera_id} · full-frame customer grid</div>
      <div className="grid" style={{ gridTemplateColumns: `repeat(${g.gw || 24}, 1fr)`, gap: 1 }}>
        {Array.from({ length: (g.gw || 24) * (g.gh || 14) }).map((_, i) => {
          const r = Math.floor(i / (g.gw || 24)), c = i % (g.gw || 24);
          const v = (cells[`${r}_${c}`] || 0) / mx;
          const col = v >= 0.6 ? BAND.hot : v >= 0.3 ? BAND.warm : v >= 0.1 ? BAND.moderate : v > 0 ? BAND.cold : "#1C1C1E";
          return <div key={i} style={{ background: col, aspectRatio: "1" }} />;
        })}
      </div>
    </div>
  );
}

function Heat({ h }) {
  const groups = {};
  h.zones.forEach((z) => { const k = (z.camera_ids || [])[0] || "?"; (groups[k] = groups[k] || []).push(z); });
  return (
    <div className={box} data-testid="avd-heatmap">
      <div className={h2}>Customer-only heat map <span className="text-[10px] text-[#71717A] font-mono-data">red hot · orange warm · yellow moderate · grey cold · hatched blind spot · dark = excluded (no data)</span></div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {Object.entries(groups).map(([cam, zs]) => (
          <div key={cam}><div className="text-[10px] font-mono-data text-[#71717A] mb-1">{cam}</div>
            <div className="grid grid-cols-2 gap-1">{zs.map((z) => <ZoneTile key={z.zone_id} z={z} />)}</div></div>
        ))}
      </div>
      {h.camera_grids?.length > 0 && <div className="grid md:grid-cols-3 gap-3 mt-4">{h.camera_grids.map((g) => <CamGrid key={g.camera_id} g={g} />)}</div>}
    </div>
  );
}

function Slabs({ s, onRun }) {
  return (
    <div className={box} data-testid="avd-slabs">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-bold text-sm">Granite slab count · {s.date} {s.last_run_at && <span className="text-[10px] text-[#71717A] font-mono-data">last run {new Date(s.last_run_at).toLocaleTimeString()}</span>}</div>
        <button data-testid="avd-run-slab-count" onClick={onRun} disabled={s.running} className="px-3 py-1 bg-[#0A84FF] disabled:opacity-40 text-white text-xs">{s.running ? "Counting…" : "Run Daily Count Now"}</button>
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {s.bays.map((b) => (
          <div key={b.bay_id} data-testid={`avd-bay-${b.bay_id}`} className="border border-white/10 px-3 py-2">
            <div className="text-[10px] text-[#71717A] font-mono-data">Bay {b.bay_id}</div>
            <div className="font-display font-bold">{b.slabs}</div>
            <div className="text-[10px] font-mono-data" style={{ color: STATUS[b.status] }}>{b.status}</div>
          </div>
        ))}
        <div className="border border-white/20 px-3 py-2"><div className="text-[10px] text-[#71717A] font-mono-data">Warehouse total</div><div className="font-display font-black" data-testid="avd-slab-total">{s.warehouse_total}</div></div>
      </div>
      <table className="w-full text-[11px] font-mono-data">
        <thead><tr className="text-[#71717A] text-left"><th>Zone</th><th>Cam</th><th>Slabs</th><th>Bundles</th><th>Conf</th><th>Status</th><th>Note</th></tr></thead>
        <tbody>{s.zones.map((z) => (
          <tr key={z.zone_id} className="border-t border-white/5" data-testid={`avd-slab-${z.zone_id}`}>
            <td>{z.zone_id}</td><td>{z.camera_id}</td><td>{z.count ?? "—"}</td><td>{z.bundles ?? "—"}</td><td>{z.confidence}</td>
            <td style={{ color: STATUS[z.status] }}>{z.status}</td><td className="text-[#71717A]">{z.note}</td>
          </tr>))}</tbody>
      </table>
      {!s.zones.length && <div className="text-xs text-[#71717A]">Not counted yet today.</div>}
    </div>
  );
}

function Cash({ items, onReview }) {
  return (
    <div className={box} data-testid="avd-cash">
      <div className={h2}>Suspicious cash activity — needs human review ({items.filter((i) => !i.reviewed).length})</div>
      {!items.length && <div className="text-xs text-[#71717A]">No cash events.</div>}
      {items.map((e) => (
        <div key={e.id} className="flex items-center justify-between border-t border-white/5 py-1.5 text-[11px] font-mono-data" data-testid={`avd-cash-${e.id}`}>
          <span className={e.reviewed ? "text-[#52525B] line-through" : "text-[#FF9F0A]"}>{e.message} · {e.status}</span>
          {!e.reviewed && <button data-testid={`avd-cash-review-${e.id}`} onClick={() => onReview(e.id)} className="text-[#30D5C8] hover:text-white">Mark reviewed</button>}
        </div>
      ))}
    </div>
  );
}

function CashSessions({ items }) {
  const [img, setImg] = useState({});
  const show = async (id) => {
    try { const r = await api.get(`/avstone/cash-events/${id}/snapshot`); setImg((m) => ({ ...m, [id]: r.data.image })); }
    catch (e) { toast.error("No snapshot kept for this one"); }
  };
  return (
    <div className={box} data-testid="avd-cash-sessions">
      <div className={h2}>Cash handled with no customer present — today ({items.length})</div>
      {!items.length && <div className="text-xs text-[#71717A]">None today.</div>}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {items.map((c) => (
          <div key={c.id} className="border border-white/10 p-2" data-testid={`avd-cash-session-${c.id}`}>
            <div className="text-[11px] font-mono-data text-white">{c.time} · {c.who}</div>
            {c.ai && <div className="text-[10px] font-mono-data text-[#71717A]">AI second opinion: {c.ai}</div>}
            {img[c.id] ? <img src={img[c.id]} alt="desk snapshot" className="mt-1 w-full" data-testid={`avd-cash-snapshot-${c.id}`} />
              : c.has_snapshot && <button data-testid={`avd-cash-snapshot-btn-${c.id}`} onClick={() => show(c.id)} className="mt-1 text-[11px] font-mono-data text-[#0A84FF] hover:text-white">View snapshot</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Health({ d }) {
  return (
    <div className={box} data-testid="avd-health">
      <div className={h2}>Camera health & drift</div>
      {!d.camera_failures.length && !d.drift_warnings.length && <div className="text-xs text-[#30D158]">All cameras healthy.</div>}
      {d.drift_warnings.map((c) => <div key={"d" + c.camera_id} className="text-[11px] font-mono-data text-[#FF3B30]">{c.camera_id} — DRIFT (view moved from reference)</div>)}
      {d.camera_failures.map((c) => <div key={c.camera_id} className="text-[11px] font-mono-data text-[#A1A1AA]">{c.camera_id} {c.name} — {c.health}</div>)}
    </div>
  );
}

const CFG_FIELDS = [
  ["cash_hours_start", "Cashier hours start"], ["cash_hours_end", "Cashier hours end"], ["slab_time", "Daily slab count time"],
  ["digest_time", "Digest (close) time"], ["greet_delay_s", "Greet delay (s)"], ["whatsapp_to", "Digest WhatsApp to (+1...)"],
  ["floor_whatsapp_to", "Floor WhatsApp to"], ["whatsapp_from", "Twilio WhatsApp from"], ["twilio_sid", "Twilio SID"], ["twilio_token", "Twilio token"],
];

function Settings() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/avstone/config").then((r) => setC(r.data)).catch(() => {}); }, []);
  if (!c) return null;
  const save = async () => {
    try { const r = await api.put("/avstone/config", { ...c, greet_delay_s: Number(c.greet_delay_s) }); setC(r.data); toast.success("Saved"); } catch (e) { toast.error("Save failed"); }
  };
  return (
    <div className={box} data-testid="avd-settings">
      <div className={h2}>Settings (A V Stone only)</div>
      <div className="grid md:grid-cols-5 gap-2">
        {CFG_FIELDS.map(([k, l]) => (
          <label key={k} className="text-[10px] font-mono-data text-[#71717A]">{l}
            <input data-testid={`avd-cfg-${k}`} type={k === "twilio_token" ? "password" : "text"} value={c[k] ?? ""} onChange={(e) => setC({ ...c, [k]: e.target.value })}
              className="w-full mt-0.5 bg-[#0A0A0C] border border-white/15 text-white text-xs px-2 py-1" /></label>
        ))}
      </div>
      <label className="text-xs font-mono-data text-[#A1A1AA] flex items-center gap-2 mt-2">
        <input data-testid="avd-cfg-whatsapp-enabled" type="checkbox" checked={!!c.whatsapp_enabled} onChange={(e) => setC({ ...c, whatsapp_enabled: e.target.checked })} /> WhatsApp enabled
      </label>
      <label className="text-xs font-mono-data text-[#A1A1AA] flex items-center gap-2 mt-1">
        <input data-testid="avd-cfg-clip-retention" type="checkbox" checked={!!c.clip_retention} onChange={(e) => setC({ ...c, clip_retention: e.target.checked })} /> Keep desk snapshot with each cash flag
      </label>
      <button data-testid="avd-cfg-save" onClick={save} className="mt-2 px-3 py-1 bg-[#0A84FF] text-white text-xs">Save settings</button>
    </div>
  );
}

export default function AvStoneDashboard() {
  const [d, setD] = useState(null), [h, setH] = useState(null), [s, setS] = useState(null), [cash, setCash] = useState([]);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    api.get("/avstone/digest").then((r) => setD(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Access denied"));
    api.get("/avstone/heatmap").then((r) => setH(r.data)).catch(() => {});
    api.get("/avstone/slabs").then((r) => setS(r.data)).catch(() => {});
    api.get("/avstone/cash-events").then((r) => setCash(r.data || [])).catch(() => {});
  }, []);
  useEffect(load, [load]);
  useEffect(() => {
    if (!s?.running) return undefined;
    const t = setInterval(() => api.get("/avstone/slabs").then((r) => { setS(r.data); if (!r.data.running) load(); }), 4000);
    return () => clearInterval(t);
  }, [s?.running, load]);
  const runSlabs = async () => { await api.post("/avstone/slabs/run"); setS({ ...s, running: true }); toast("Slab count started"); };
  const review = async (id) => { await api.post(`/avstone/cash-events/${id}/review`, { note: "reviewed" }); load(); };
  const send = async () => { const r = await api.post("/avstone/digest/send"); toast(`WhatsApp: ${r.data.whatsapp}`); };

  if (err) return <div className="p-6 text-[#FF3B30] font-mono-data text-sm" data-testid="avd-error">{err}</div>;
  if (!d) return <div className="p-6 text-[#71717A] text-sm">Loading…</div>;
  return (
    <div data-testid="avstone-dashboard" className="p-6 space-y-4">
      <h1 className="font-display font-black text-2xl">A V Stone Corp — Daily Dashboard · {d.date}</h1>
      <div className="grid lg:grid-cols-2 gap-4"><Customers d={d} /><Health d={d} /></div>
      <Workers items={d.workers || []} onChange={load} />
      {h && <Heat h={h} />}
      {s && <Slabs s={s} onRun={runSlabs} />}
      <Cash items={cash} onReview={review} />
      <CashSessions items={d.cash_sessions_no_customer || []} />
      <div className={box} data-testid="avd-digest">
        <div className="flex items-center justify-between mb-2"><div className="font-display font-bold text-sm">Daily digest (WhatsApp at close)</div>
          <button data-testid="avd-digest-send" onClick={send} className="px-3 py-1 border border-white/20 text-xs">Send now</button></div>
        <pre className="text-[11px] font-mono-data text-[#A1A1AA] whitespace-pre-wrap" data-testid="avd-digest-text">{d.text}</pre>
      </div>
      <Settings />
    </div>
  );
}
